import React, {
  createContext,
  useContext,
  ReactElement,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useCustomIdleTimer } from "../hooks/common/useIdleTimer";

interface IdleTimerContextType {
  isActive: boolean;
  getRemainingTime: () => number;
  getElapsedTime: () => number;
  reset: () => void;
}

const IdleTimerContext = createContext<IdleTimerContextType | null>(null);

interface IdleTimerProviderProps {
  children: ReactElement;
}

export const IdleTimerProvider: React.FC<IdleTimerProviderProps> = ({
  children,
}) => {
  const { loggedIn, logout } = useAuth();
  const [isActive, setIsActive] = useState(true);

  const idleTimer = useCustomIdleTimer({
    onIdle: logout,
    isLoggedIn: loggedIn,
    onStateChange: (active) => {
      setIsActive(active);
    },
  });

  const contextValue: IdleTimerContextType = {
    isActive,
    getRemainingTime: () => idleTimer.getRemainingTime(),
    getElapsedTime: () => idleTimer.getElapsedTime(),
    reset: () => {
      idleTimer.reset();
      setIsActive(true); // Reset to active state
    },
  };

  return (
    <IdleTimerContext.Provider value={contextValue}>
      {children}
    </IdleTimerContext.Provider>
  );
};

export const useIdleTimerContext = () => {
  const context = useContext(IdleTimerContext);
  if (!context) {
    throw new Error(
      "useIdleTimerContext must be used within an IdleTimerProvider",
    );
  }
  return context;
};
